"use client";

import { useState, type FormEvent } from "react";
import { ArrowRight, Eye, EyeOff, LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";

export function SignInForm({ next }: { next: string }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  async function signIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
        cache: "no-store",
      });
      if (!response.ok) {
        const data = (await response.json()) as { error?: string };
        setError(data.error || "Sign-in failed. Please try again.");
        setSubmitting(false);
        return;
      }
      window.location.assign(next);
    } catch {
      setError("Couldn't connect. Please try again.");
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={signIn} className="mt-9" autoComplete="on">
      <label htmlFor="reelay-passcode" className="block text-[12px] font-medium text-text-muted">
        Passcode
      </label>
      <div className="relative mt-2">
        <LockKeyhole aria-hidden className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-text-faint" />
        <input
          id="reelay-passcode"
          name="password"
          type={showPassword ? "text" : "password"}
          inputMode="numeric"
          autoComplete="current-password"
          autoFocus
          required
          maxLength={128}
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            if (error) setError("");
          }}
          placeholder="Enter your passcode"
          aria-invalid={!!error}
          aria-describedby={error ? "signin-error" : undefined}
          className="h-12 w-full rounded-[var(--radius-control)] border border-border-strong bg-bg pl-11 pr-12 text-[15px] text-text outline-none placeholder:text-text-faint"
        />
        <button
          type="button"
          onClick={() => setShowPassword((visible) => !visible)}
          aria-label={showPassword ? "Hide passcode" : "Show passcode"}
          className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-text-faint transition-colors hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink/60"
        >
          {showPassword ? <EyeOff className="h-[17px] w-[17px]" /> : <Eye className="h-[17px] w-[17px]" />}
        </button>
      </div>

      {error && <p id="signin-error" role="alert" className="mt-3 text-[12px] text-danger">{error}</p>}

      <Button type="submit" size="lg" disabled={submitting} className="mt-6 h-12 w-full justify-between px-4 text-[14px]">
        <span>{submitting ? "Opening workspace…" : "Enter workspace"}</span>
        <ArrowRight aria-hidden className="h-4 w-4" />
      </Button>

      <p className="mt-7 border-t border-border pt-5 text-[11px] leading-relaxed text-text-faint">
        This workspace is private. Your session stays signed in on this device.
      </p>
    </form>
  );
}
